<template>
  <SoWidget title="Deposit">
    <SoSkeleton
      v-if="current.depositStatusDetails.isFetching"
      class="text-h4 pb-1"
    />
    <button
      v-else
      class="show-details-btn d-flex justify-space-between align-center mt-6 w-100"
      @click="showDetails = !showDetails"
    >
      <SoStatus :status="depositStatus" />
      <SoIcon
        v-if="!noDepositDetails"
        :icon="showDetails ? 'chevron-up' : 'chevron-down'"
        size="small"
        class="ml-2"
      />
    </button>
    <template #expand>
      <div v-show="showDetails && !noDepositDetails" class="mx-6 my-2">
        <DepositRequestDetails
          v-if="depositTransaction || onlyShowDeadlineDate"
          ref="financials-deposit-details-modal"
          :deposit-request="{
            deposit: depositTransaction,
            refund: refundTransaction,
            deadline: currentDepositStatus?.depositDeadline,
            onlyShowDeadline: onlyShowDeadlineDate,
          }"
        />
      </div>
    </template>
  </SoWidget>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { Status, StatusType } from "@/types/Status"
  import DepositRequestDetails from "@/views/customer/financials/components/DepositRequestDetails.vue"
  import dayjs from "dayjs"

  export default defineComponent({
    components: {
      DepositRequestDetails,
      SoWidget,
      SoSkeleton,
      SoIcon,
      SoStatus,
    },
    data() {
      return {
        showDetails: false,
        current: useCurrentAccountStore(),
      }
    },
    computed: {
      currentDepositStatus() {
        const depositStatus = this.current.depositStatusDetails.data.result?.[0]
        return depositStatus
      },
      depositTransaction() {
        return this.current.transactions.data.find((transaction) =>
          transaction.transactionType.includes("Deposit"),
        )
      },
      refundTransaction() {
        return this.current.transactions.data.find(
          (transaction) =>
            transaction.isCredit &&
            transaction?.reference.startsWith("[Refund]") &&
            this.depositTransaction &&
            transaction?.reference.includes(this.depositTransaction.reference),
        )
      },
      depositStatus() {
        const depositStatus = this.currentDepositStatus
        let statusType: StatusType
        let statusName
        const depositTransaction = this.depositTransaction
        const refundTransaction = this.refundTransaction

        if (
          depositStatus?.depositDeadline?.isAfter(dayjs(), "day") &&
          !depositTransaction &&
          !depositStatus?.depositPaid
        ) {
          statusType = "caution"
          statusName = "Deposit requested"
        } else if (
          depositStatus?.depositDeadline?.isBefore(dayjs(), "day") &&
          !depositStatus?.depositPaid &&
          !depositTransaction
        ) {
          statusType = "critical"
          statusName = "Deposit not paid"
        } else if (
          depositStatus?.depositPaid &&
          depositTransaction &&
          !refundTransaction
        ) {
          statusType = "normal"
          statusName = "Deposit held"
        } else if (
          depositStatus?.depositPaid &&
          depositTransaction &&
          refundTransaction
        ) {
          statusType = "neutral"
          statusName = "Deposit refunded"
        } else {
          statusType = "default"
          statusName = "Deposit not required"
        }

        return new Status(statusType, statusName)
      },
      noDepositDetails() {
        const statusType = this.depositStatus.type
        return ["default"].includes(statusType)
      },
      onlyShowDeadlineDate() {
        const statusType = this.depositStatus.type
        return ["critical", "caution"].includes(statusType)
      },
    },
  })
</script>

<style scoped>
  .show-details-btn:focus-visible {
    outline: var(--theme-outline);
    border-radius: var(--theme-radius);
  }
</style>
